import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useLanguage } from '../../../../features/language/useLanguage';

interface NotificationDivergenceBannerProps {
  count: number;
  resyncing: boolean;
  onResync: () => void;
}

/** Warns when pending work orders no longer match the configured reminders, with a re-sync action. */
export function NotificationDivergenceBanner({ count, resyncing, onResync }: NotificationDivergenceBannerProps) {
  const { t } = useLanguage();
  return (
    <div className="px-4 sm:px-6 pt-4">
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-900">
            {t('notifications.settings.divergenceTitle', undefined, 'Existing work orders are out of sync')}
          </p>
          <p className="text-xs text-amber-800 mt-0.5">
            {t(
              'notifications.settings.divergenceDescription',
              { count },
              `${count} pending work order(s) don't match the current reminders.`
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onResync}
          disabled={resyncing}
          data-test-id="notification-resync-button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-amber-300 bg-white text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
        >
          <RefreshCw className={`h-4 w-4 ${resyncing ? 'animate-spin' : ''}`} />
          {resyncing ? t('common.loading', undefined, 'Loading…') : t('notifications.settings.resyncButton', undefined, 'Re-sync')}
        </button>
      </div>
    </div>
  );
}
